import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useTheme } from 'styled-components';

import { CalendarIcon, CrossmarkIcon } from 'assets/icons';
import { useFormikField } from 'common/components/form/useFormikField';
import { FieldLabel } from 'common/components/form/Wrapper/FieldLabel';
import { Error } from 'common/components/form/Wrapper/Wrapper.styled';
import { useScreenSizeDetection } from 'common/hooks';
import { DATE_FORMAT } from 'consts/placeholders';

import { handleOnKeyDown } from './helper';
import { IDateTimeField } from './IDateTimeField';
import { DatePickerStyled, DatePickerWrapper } from './index.styled';

export const DateField = ({
  name,
  label,
  dateFormat,
  additionalStyle,
  removeRightIcon,
  hasErrorMargin,
  ...rest
}: IDateTimeField) => {
  const { value, setValue, hasError, error } = useFormikField(name);

  const { t } = useTranslation();
  const { colors } = useTheme();
  const { isMobile } = useScreenSizeDetection();

  const { disableClearIcon, placeholder } = rest;

  const onChange = (date: any) => {
    setValue(date);
    if (rest.onChange) {
      rest.onChange(date);
    }
  };

  return (
    <DatePickerWrapper>
      <FieldLabel
        label={label}
        name={name}
        isRequired={rest.isRequired}
        hasError={!!error}
        tooltip={rest.labelIcon}
        justifyContent={rest.justifyContentLabel}
      />

      <DatePickerStyled
        {...rest}
        additionalStyle={additionalStyle}
        placeholder={placeholder || t('Select...')}
        format={dateFormat || DATE_FORMAT}
        name={name}
        value={!!value ? dayjs(value) : null}
        onChange={onChange}
        disabled={rest.disabled}
        onKeyDown={e => handleOnKeyDown(e, setValue)}
        disabledDate={rest.disabledDate}
        suffixIcon={
          !removeRightIcon ? <CalendarIcon width={24} height={24} fill={colors.black_40} /> : null
        }
        allowClear={{
          clearIcon: !disableClearIcon && (
            <CrossmarkIcon width={24} height={24} fill={colors.black_40} />
          ),
        }}
        $hasError={hasError}
        inputReadOnly={isMobile}
      />

      {hasError && (
        <Error margin={hasError ? (hasErrorMargin ? hasErrorMargin : '-1.25rem 0 1rem 0') : null}>
          {t(String(error))}
        </Error>
      )}
    </DatePickerWrapper>
  );
};
